import React, { useState, useRef, useEffect } from "react";
import axios from "axios";

const MetaForm = () => {
  const [inputText, setInputText] = useState("");
  const [clusters, setClusters] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const clusterRef = useRef(null);
  const [keywordAnalysis, setKeywordAnalysis] = useState(null);

  useEffect(() => {
    if (clusters && clusterRef.current) {
      clusterRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [clusters]);

  const extractKeywords = (text) => {
    // Tokenize the text into words
    const words = text.toLowerCase().match(/\b[\w-]+\b/g);
    if (!words) return [];

    // Generate n-grams (2 to 5 words)
    const nGrams = [];
    for (let n = 2; n <= 5; n++) {
      for (let i = 0; i <= words.length - n; i++) {
        nGrams.push(words.slice(i, i + n).join(" "));
      }
    }

    // Count n-gram frequencies
    const nGramCounts = nGrams.reduce((acc, nGram) => {
      acc[nGram] = (acc[nGram] || 0) + 1;
      return acc;
    }, {});

    // Calculate TF-IDF-like score with length bonus
    const totalNGrams = Object.values(nGramCounts).reduce((a, b) => a + b, 0);
    const nGramScores = Object.entries(nGramCounts).map(([nGram, count]) => {
      const tf = count / totalNGrams;
      const words = nGram.split(" ");
      const idf = Math.log(totalNGrams / count);
      const lengthBonus = Math.log(words.length + 1); // Favor longer phrases
      return [nGram, tf * idf * lengthBonus];
    });

    // Sort by score and take top 50
    const sortedNGrams = nGramScores
      .sort((a, b) => b[1] - a[1])
      .slice(0, 50)
      .map(([nGram]) => nGram);

    // Filter out substrings and ensure minimum word count
    const filteredNGrams = sortedNGrams.filter((nGram, index) => {
      const wordCount = nGram.split(" ").length;
      return (
        wordCount >= 2 &&
        !sortedNGrams.some((otherNGram, otherIndex) => {
          return (
            index !== otherIndex &&
            otherNGram.includes(nGram) &&
            otherNGram.split(" ").length > wordCount
          );
        })
      );
    });

    // Remove duplicates and near-duplicates
    const uniqueNGrams = [];
    for (const nGram of filteredNGrams) {
      if (
        !uniqueNGrams.some(
          (existingNGram) =>
            nGram.includes(existingNGram) || existingNGram.includes(nGram)
        )
      ) {
        uniqueNGrams.push(nGram);
      }
    }

    return uniqueNGrams;
  };

  const generateClusters = async () => {
    setIsLoading(true);
    setError(null);

    const extractedKeywords = extractKeywords(inputText);
    console.log("Extracted keywords:", extractedKeywords);

    try {
      const response = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        {
          model: "gpt-4o-mini",
          messages: [
            {
              role: "system",
              content:
                "You are an AI assistant specialized in advanced semantic analysis, keyword clustering, and SEO optimization. Your task is to identify, expand upon, and group highly relevant, SEO-friendly keywords and phrases into numerous, diverse clusters.",
            },
            {
              role: "user",
              content: `Analyze the following keywords and create comprehensive semantic clusters optimized for SEO. Follow these guidelines:

1. Create at least 10-20 clusters, or more if the content warrants it.
2. Focus on specific, meaningful long-tail keywords (3-5 words) with clear search intent.
3. Expand upon the given keywords by suggesting additional relevant terms and phrases.
4. Include a mix of informational, navigational, and transactional keywords where appropriate.
5. Consider search volume, competition, and user intent - prioritize keywords that balance relevance with potential traffic.
6. Include up to 20 keywords per cluster if relevant.
7. Avoid generic or vague terms - each keyword should be specific and targeted.
8. Include relevant LSI (Latent Semantic Indexing) keywords to improve topical relevance.
9. Ensure clusters cover a wide range of subtopics related to the main theme.

Each cluster should have a descriptive name that reflects the specificity of the keywords it contains. Provide a brief explanation for each cluster, highlighting its SEO value and relevance to the topic.

Present the result as a JSON object where keys are cluster names, and values are objects containing an array of keywords and an explanation. Do not include any markdown formatting in your response.

Keywords: ${extractedKeywords.join(", ")}

Example format:
{
  "Specific SEO-Optimized Cluster Name 1": {
    "keywords": ["high-value long tail keyword 1", "specific SEO phrase 2", "user intent focused keyword 3", ...],
    "explanation": "Brief explanation of this cluster, highlighting its SEO value, search intent, and relevance to the overall topic."
  },
  "Specific SEO-Optimized Cluster Name 2": {
    "keywords": ["another set of keywords", "more specific phrases", "additional relevant terms", ...],
    "explanation": "Explanation for this cluster, emphasizing its importance in the content hierarchy."
  },
  ...
}

Ensure that the cluster names, keywords, and explanations are tailored for maximum SEO impact while maintaining high relevance to the source content. Each keyword should be unique and valuable for content creation and optimization. Aim to provide a comprehensive analysis with numerous, diverse clusters.`,
            },
          ],
        },
        {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
            "Content-Type": "application/json",
          },
        }
      );

      const content = response.data.choices[0].message.content;
      console.log("Raw API response:", content);

      const cleanedContent = content.replace(/```json\s?|\s?```/g, "").trim();
      console.log("Cleaned content:", cleanedContent);

      let parsedClusters;
      try {
        parsedClusters = JSON.parse(cleanedContent);
      } catch (parseError) {
        console.error("JSON parsing error:", parseError);

        // Fallback parsing method
        parsedClusters = {};
        const clusterRegex = /"([^"]+)":\s*{([^}]+)}/g;
        let match;
        while ((match = clusterRegex.exec(cleanedContent)) !== null) {
          const clusterName = match[1];
          const clusterContent = match[2];
          const keywordsMatch = clusterContent.match(/"keywords":\s*\[(.*?)\]/);
          const explanationMatch = clusterContent.match(
            /"explanation":\s*"(.*?)"/
          );

          if (keywordsMatch && explanationMatch) {
            parsedClusters[clusterName] = {
              keywords: keywordsMatch[1]
                .split(",")
                .map((k) => k.trim().replace(/"/g, "")),
              explanation: explanationMatch[1],
            };
          }
        }

        if (Object.keys(parsedClusters).length === 0) {
          throw new Error("Failed to parse clusters using fallback method");
        }
      }

      console.log("Parsed clusters:", parsedClusters);
      setClusters(parsedClusters);
    } catch (error) {
      console.error("Error clustering keywords:", error);
      setError(`Failed to generate clusters. ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!clusters) return;

    let csvContent = "Cluster Name,Keyword,Explanation\n";

    Object.entries(clusters).forEach(([clusterName, clusterData]) => {
      const explanation = clusterData.explanation.replace(/"/g, '""'); // Escape quotes
      clusterData.keywords.forEach((keyword) => {
        csvContent += `"${clusterName}","${keyword}","${explanation}"\n`;
      });
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "keyword_clusters.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const analyzeKeywords = (text) => {
    const stopWords = new Set([
      "the",
      "to",
      "a",
      "an",
      "and",
      "or",
      "but",
      "in",
      "on",
      "at",
      "for",
      "of",
      "with",
      "by",
      "from",
      "up",
      "about",
      "into",
      "over",
      "after",
      "is",
      "are",
      "was",
      "were",
      "be",
      "been",
      "being",
      "have",
      "has",
      "had",
      "do",
      "does",
      "did",
      "will",
      "would",
      "shall",
      "should",
      "can",
      "could",
      "may",
      "might",
      "must",
      "ought",
      "i",
      "you",
      "he",
      "she",
      "it",
      "we",
      "they",
      "them",
      "their",
      "this",
      "that",
      "these",
      "those",
      "am",
      "is",
      "are",
      "was",
      "were",
      "be",
      "been",
      "being",
      "have",
      "has",
      "had",
      "do",
      "does",
      "did",
      "will",
      "would",
      "shall",
      "should",
      "can",
      "could",
      "may",
      "might",
      "must",
      "ought",
      "i'm",
      "you're",
      "he's",
      "she's",
      "it's",
      "we're",
      "they're",
      "i've",
      "you've",
      "we've",
      "they've",
      "i'd",
      "you'd",
      "he'd",
      "she'd",
      "we'd",
      "they'd",
      "i'll",
      "you'll",
      "he'll",
      "she'll",
      "we'll",
      "they'll",
      "isn't",
      "aren't",
      "wasn't",
      "weren't",
      "hasn't",
      "haven't",
      "hadn't",
      "doesn't",
      "don't",
      "didn't",
      "won't",
      "wouldn't",
      "shan't",
      "shouldn't",
      "can't",
      "cannot",
      "couldn't",
      "mustn't",
      "let's",
      "that's",
      "who's",
      "what's",
      "here's",
      "there's",
      "when's",
      "where's",
      "why's",
      "how's",
      "a",
      "an",
      "the",
      "and",
      "or",
      "but",
      "in",
      "on",
      "at",
      "to",
      "for",
      "of",
      "with",
      "by",
      "from",
      "up",
      "down",
      "out",
      "off",
      "over",
      "under",
      "again",
      "further",
      "then",
      "once",
      "here",
      "there",
      "when",
      "where",
      "why",
      "how",
      "all",
      "any",
      "both",
      "each",
      "few",
      "more",
      "most",
      "other",
      "some",
      "such",
      "no",
      "nor",
      "not",
      "only",
      "own",
      "same",
      "so",
      "than",
      "too",
      "very",
      "s",
      "t",
      "can",
      "will",
      "just",
      "don",
      "should",
      "now",
      "d",
      "ll",
      "m",
      "o",
      "re",
      "ve",
      "y",
      "ain",
      "aren",
      "couldn",
      "didn",
      "doesn",
      "hadn",
      "hasn",
      "haven",
      "isn",
      "ma",
      "mightn",
      "mustn",
      "needn",
      "shan",
      "shouldn",
      "wasn",
      "weren",
      "won",
      "wouldn",
      "your",
      "yours",
      "yourself",
      "yourselves",
      "him",
      "himself",
      "her",
      "herself",
      "its",
      "itself",
      "they",
      "them",
      "their",
      "theirs",
      "themselves",
      "what",
      "which",
      "who",
      "whom",
      "this",
      "that",
      "these",
      "those",
      "am",
      "is",
      "are",
      "was",
      "were",
      "be",
      "been",
      "being",
      "have",
      "has",
      "had",
      "doing",
      "as",
      "if",
      "because",
      "till",
      "until",
      "while",
      "of",
      "at",
      "by",
      "for",
      "with",
      "about",
      "against",
      "between",
      "into",
      "through",
      "during",
      "before",
      "after",
      "above",
      "below",
      "to",
      "from",
      "up",
      "down",
      "in",
      "out",
      "on",
      "off",
      "over",
      "under",
      "again",
      "further",
      "then",
      "once",
      "here",
      "there",
      "when",
      "where",
      "why",
      "how",
      "all",
      "any",
      "both",
      "each",
      "few",
      "more",
      "most",
      "other",
      "some",
      "such",
      "no",
      "nor",
      "not",
      "only",
    ]);

    const words = text.toLowerCase().match(/\b[\w-]+\b/g) || [];
    const wordCount = words.length;
    const wordFrequency = words.reduce((acc, word) => {
      if (!stopWords.has(word)) {
        acc[word] = (acc[word] || 0) + 1;
      }
      return acc;
    }, {});

    const sortedFrequency = Object.entries(wordFrequency)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    setKeywordAnalysis({
      wordCount,
      topKeywords: sortedFrequency.map(([word, count]) => ({
        word,
        count,
        density: ((count / wordCount) * 100).toFixed(2),
      })),
    });
  };

  const handleInputChange = (e) => {
    const newText = e.target.value;
    setInputText(newText);
    analyzeKeywords(newText);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="card bg-base-100 shadow-xl mb-8">
        <div className="card-body">
          <h2 className="card-title text-2xl mb-4 text-primary">
            Keyword Input
          </h2>
          <textarea
            className="textarea textarea-bordered w-full h-48 mb-4"
            placeholder="Enter your keywords here, separated by commas or new lines"
            value={inputText}
            onChange={handleInputChange}
          ></textarea>
          {keywordAnalysis && (
            <div className="mb-4">
              <p>Word Count: {keywordAnalysis.wordCount}</p>
              <h3 className="font-semibold mt-2">Top 10 Keywords:</h3>
              <ul>
                {keywordAnalysis.topKeywords.map(({ word, count, density }) => (
                  <li key={word}>
                    {word}: {count} ({density}%)
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex justify-center">
            <button
              className={`btn btn-primary ${isLoading ? "btn-disabled" : ""}`}
              onClick={generateClusters}
              disabled={isLoading || !inputText.trim()}
            >
              {isLoading ? (
                <>
                  <span className="loading loading-spinner loading-sm mr-2"></span>
                  Generating Clusters...
                </>
              ) : (
                "Generate Clusters"
              )}
            </button>
          </div>
          {error && <div className="alert alert-error mt-4">{error}</div>}
        </div>
      </div>

      {clusters && (
        <div ref={clusterRef} className="card bg-base-100 shadow-xl mb-8">
          <div className="card-body">
            <div className="flex justify-between items-center mb-6">
              <h2 className="card-title text-2xl text-primary">Clusters</h2>
              <button
                className="btn btn-secondary btn-sm"
                onClick={exportToCSV}
              >
                Export to CSV
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(clusters).map(([clusterName, clusterData]) => (
                <div key={clusterName} className="card bg-base-200 shadow-md">
                  <div className="card-body">
                    <h3 className="card-title text-lg mb-2 text-secondary">
                      {clusterName}
                    </h3>
                    <p className="text-base-content mb-4 text-sm">
                      {clusterData.explanation}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {clusterData.keywords.map((keyword, index) => (
                        <span
                          key={index}
                          className="badge badge-primary badge-outline break-words text-xs"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetaForm;
